import { IconSearch } from '@tabler/icons-solidjs'
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  type JSX,
  onCleanup,
  onMount,
  Show,
  splitProps,
} from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv } from '../lib/tv.ts'
import { SparkIcon } from './icons.tsx'
import { Kbd } from './kbd.tsx'

/**
 * ⌘K. The primary way to reach anything in this system, and the place the assistant lives.
 *
 * Design notes that are not obvious from the code:
 *
 *   · NO open/close animation. This is opened by keyboard dozens of times a shift; an animation
 *     on a keyboard-initiated action reads as lag and makes the whole app feel slow. Raycast has
 *     none for the same reason.
 *   · The last row is always "ask the assistant", and it is the SELECTED row when nothing else
 *     matches. That is what makes this an AI-first surface rather than a menu with a chat bolted
 *     on: typing a sentence is a first-class outcome, not a fallback.
 *   · Whatever the assistant does with that sentence comes back as an AiDraft. Nothing typed here
 *     writes to the ledger directly — see AGENTS.md ground rule 2.
 *   · Matching is substring, case-folded, over label + keywords. Fuzzy matching feels clever and
 *     then surfaces "Списати матеріал" when someone types "склад", which in an accounting tool is
 *     worse than no match at all.
 */
const palette = tv({
  slots: {
    root: [
      /* Sits high rather than centred — the eye is already at the top of the screen when the
         shortcut is pressed, and a centred palette makes the results jump down the page.
         `mx-auto` is load-bearing: a native <dialog> is not centred by default. */
      'mx-auto mt-[12vh] mb-auto w-[calc(100%-2rem)] max-w-xl rounded-panel bg-surface p-0 text-text',
      'shadow-overlay backdrop:bg-ink/40 backdrop:backdrop-blur-[2px]',
    ],
    field: 'flex items-center gap-2.5 border-b border-border px-3.5',
    input: 'h-11 w-full bg-transparent text-ui text-text outline-none placeholder:text-text-subtle',
    list: 'max-h-[min(24rem,50vh)] overflow-y-auto p-1.5',
    group: 'px-2.5 pt-2.5 pb-1 text-micro font-semibold text-text-subtle uppercase',
    item: [
      'flex w-full cursor-default items-center gap-2.5 rounded-control px-2.5 py-2 text-left',
      'text-body text-text transition-colors duration-(--duration-press) ease-out',
    ],
    itemActive: 'bg-accent-soft text-accent',
    itemLabel: 'min-w-0 flex-1 truncate',
    itemHint: 'shrink-0 text-body text-text-subtle',
    footer:
      'flex items-center justify-between gap-3 border-t border-border bg-surface-muted px-3.5 py-2 text-micro text-text-subtle',
  },
})

export interface CommandItem {
  id: string
  label: string
  /** Section heading. Items are rendered in the order given, grouped by first appearance. */
  group: string
  /** Extra match terms — a Ukrainian synonym, an English one, a module code. */
  keywords?: string
  /** Right-aligned hint: a shortcut, a count, a destination. */
  hint?: string
  icon?: JSX.Element
  run: () => void
}

export interface CommandPaletteProps {
  open: boolean
  onClose: () => void
  items: CommandItem[]
  placeholder?: string
  /**
   * Called with the raw query when the operator chooses "ask the assistant". Leave undefined and
   * the row is not offered — better than offering an assistant that does nothing.
   */
  onAsk?: (query: string) => void
  askLabel?: string
}

const ASK_ID = '__ask__'

export const CommandPalette = (props: CommandPaletteProps) => {
  const [local] = splitProps(props, [
    'open',
    'onClose',
    'items',
    'placeholder',
    'onAsk',
    'askLabel',
  ])
  const styles = palette()
  const [query, setQuery] = createSignal('')
  const [cursor, setCursor] = createSignal(0)
  let element!: HTMLDialogElement
  let field!: HTMLInputElement

  const matches = createMemo(() => {
    const needle = query().trim().toLocaleLowerCase('uk')
    if (!needle) return local.items
    return local.items.filter((item) =>
      `${item.label} ${item.keywords ?? ''}`.toLocaleLowerCase('uk').includes(needle),
    )
  })

  /** The assistant row is offered whenever there is a question to ask. */
  const askRow = createMemo(() => (local.onAsk && query().trim() ? query().trim() : undefined))

  /** Flat, in render order, so ↑/↓ move through what the operator actually sees. */
  const rows = createMemo<(CommandItem | { id: typeof ASK_ID })[]>(() => {
    const ask = askRow()
    return ask ? [...matches(), { id: ASK_ID as typeof ASK_ID }] : matches()
  })

  /** Grouped for display; a Map preserves first-seen order. */
  const groups = createMemo(() => {
    const byGroup = new Map<string, CommandItem[]>()
    for (const item of matches()) {
      const bucket = byGroup.get(item.group)
      if (bucket) bucket.push(item)
      else byGroup.set(item.group, [item])
    }
    return [...byGroup]
  })

  createEffect(() => {
    if (local.open && !element.open) {
      element.showModal()
      setQuery('')
      setCursor(0)
      field.focus()
    }
    if (!local.open && element.open) element.close()
  })

  /**
   * Light dismiss. Bound imperatively rather than as an `onClick` prop because a native <dialog>
   * hosts its own backdrop: a click outside the panel lands on the dialog element itself, so this
   * is a platform behaviour rather than an interactive control. The keyboard equivalent is
   * Escape, which the platform already routes through `onCancel`.
   */
  onMount(() => {
    const dismiss = (event: MouseEvent): void => {
      if (event.target === element) local.onClose()
    }
    element.addEventListener('click', dismiss)
    onCleanup(() => element.removeEventListener('click', dismiss))
  })

  // When the result set shrinks under the cursor, snap back rather than leaving nothing selected.
  createEffect(() => {
    const max = rows().length - 1
    if (cursor() > max) setCursor(Math.max(0, max))
  })

  const runAt = (index: number): void => {
    const row = rows()[index]
    if (!row) return
    local.onClose()
    if (row.id === ASK_ID) local.onAsk?.(askRow() ?? '')
    else (row as CommandItem).run()
  }

  const onKeyDown = (event: KeyboardEvent): void => {
    const total = rows().length
    if (total === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setCursor((index) => (index + 1) % total)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setCursor((index) => (index - 1 + total) % total)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      runAt(cursor())
    }
  }

  const indexOf = (id: string): number => rows().findIndex((row) => row.id === id)

  return (
    <dialog
      ref={element}
      class={styles.root()}
      aria-label="Команди та помічник"
      onCancel={(event) => {
        event.preventDefault()
        local.onClose()
      }}
    >
      <div class={styles.field()}>
        <IconSearch
          size={16}
          strokeWidth={1.5}
          class="shrink-0 text-text-subtle"
          aria-hidden="true"
        />
        <input
          ref={field}
          class={styles.input()}
          value={query()}
          placeholder={local.placeholder ?? 'Пошук команди або питання до помічника…'}
          aria-label="Пошук команди"
          onInput={(event) => {
            setQuery(event.currentTarget.value)
            setCursor(0)
          }}
          onKeyDown={onKeyDown}
        />
        <Kbd>ESC</Kbd>
      </div>

      <div class={styles.list()}>
        <For each={groups()}>
          {([group, items]) => (
            <>
              <p class={styles.group()}>{group}</p>
              <For each={items}>
                {(item) => (
                  <button
                    type="button"
                    class={cn(styles.item(), cursor() === indexOf(item.id) && styles.itemActive())}
                    onMouseMove={() => setCursor(indexOf(item.id))}
                    onClick={() => runAt(indexOf(item.id))}
                  >
                    <Show when={item.icon}>{(icon) => icon()}</Show>
                    <span class={styles.itemLabel()}>{item.label}</span>
                    <Show when={item.hint}>
                      {(hint) => <span class={styles.itemHint()}>{hint()}</span>}
                    </Show>
                  </button>
                )}
              </For>
            </>
          )}
        </For>

        <Show when={askRow()}>
          {(ask) => (
            <>
              <p class={styles.group()}>Помічник</p>
              <button
                type="button"
                class={cn(styles.item(), cursor() === indexOf(ASK_ID) && styles.itemActive())}
                onMouseMove={() => setCursor(indexOf(ASK_ID))}
                onClick={() => runAt(indexOf(ASK_ID))}
              >
                <SparkIcon />
                <span class={styles.itemLabel()}>
                  {local.askLabel ?? 'Запитати помічника'}: {ask()}
                </span>
                <Kbd>↵</Kbd>
              </button>
            </>
          )}
        </Show>

        <Show when={rows().length === 0}>
          <p class="px-2.5 py-6 text-center text-body text-text-subtle">Нічого не знайдено</p>
        </Show>
      </div>

      <footer class={styles.footer()}>
        <span class="flex items-center gap-1.5">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd>
          навігація
          <Kbd class="ml-2">↵</Kbd>
          виконати
        </span>
        {/* Stated here rather than only in the docs: the operator needs to know, at the moment
            they ask, that the assistant proposes and a person commits. */}
        <span class="truncate">Помічник готує чернетку — записує людина</span>
      </footer>
    </dialog>
  )
}

/**
 * Binds ⌘K / Ctrl-K. Returned as a handler rather than wired to `document` inside the component,
 * so the app owns the listener's lifetime and a second palette cannot silently steal the key.
 */
export const isCommandPaletteShortcut = (event: KeyboardEvent): boolean =>
  event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)
